/**
 * API client para los endpoints admin de gestión de plantillas PDF
 * (taxor backend, /api/v1/admin/...).
 *
 * Auth: el axios `api` (lib/api.ts) ya inyecta `Authorization: Bearer
 * <accessToken>` via interceptor — los endpoints admin usan el mismo
 * JWT que el resto de operaciones del frontend.
 */

import { api } from './api'

// ─── Tipos ──────────────────────────────────────────────────────────

export type DocType = 'invoice' | 'credit_note' | 'debit_note'

export const DOC_TYPES: DocType[] = ['invoice', 'credit_note', 'debit_note']

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  invoice: 'Factura electrónica',
  credit_note: 'Nota crédito',
  debit_note: 'Nota débito',
}

export interface TaxClient {
  id: string
  name: string
  legal_name?: string
  nit: string
  contact_email: string
  api_key_prefix: string
  allowed_emitter_nits: string[]
  webhook_url?: string
  rate_limit_per_min: number
  status: 'active' | 'suspended' | 'revoked'
  created_at: string
  updated_at: string
  last_used_at?: string
}

export interface PdfTemplate {
  id?: string
  tax_client_id?: string
  doc_type: DocType
  html: string
  version: number
  is_active?: boolean
  created_by_user_id?: string
  updated_by_user_id?: string
  created_at?: string | null
  updated_at?: string | null
  source: 'db' | 'default'
}

export interface ListResponse<T> {
  items: T[]
  count: number
}

// ─── API methods ────────────────────────────────────────────────────

export const taxClientsApi = {
  /** Lista todos los tax_clients (selector). */
  list: () =>
    api.get<ListResponse<TaxClient>>('/api/v1/admin/tax-clients').then((r) => r.data),
}

export const pdfTemplatesApi = {
  /** Lista los templates activos de un tax_client (max 3). */
  list: (clientId: string) =>
    api
      .get<ListResponse<PdfTemplate>>(`/api/v1/admin/tax-clients/${clientId}/pdf-templates`)
      .then((r) => r.data),

  /**
   * Get del template de (cliente, tipo). Si no hay custom, retorna
   * el default Taxor con `source: "default"`.
   */
  get: (clientId: string, docType: DocType) =>
    api
      .get<PdfTemplate>(
        `/api/v1/admin/tax-clients/${clientId}/pdf-templates/${docType}`,
      )
      .then((r) => r.data),

  /** Crea o actualiza el template (incrementa version en update). */
  upsert: (clientId: string, docType: DocType, html: string) =>
    api
      .put<PdfTemplate>(
        `/api/v1/admin/tax-clients/${clientId}/pdf-templates/${docType}`,
        { html },
      )
      .then((r) => r.data),

  /** Elimina el template custom (vuelve al default Taxor). */
  delete: (clientId: string, docType: DocType) =>
    api
      .delete<void>(
        `/api/v1/admin/tax-clients/${clientId}/pdf-templates/${docType}`,
      )
      .then((r) => r.data),

  /**
   * Renderiza preview del template a PDF. Si `html` se pasa, se
   * usa ese (preview de edición sin guardar). Si no, se usa el
   * template guardado en DB (o default si no hay).
   *
   * Retorna un Blob — el caller lo convierte a URL para iframe.
   */
  preview: (clientId: string, docType: DocType, html?: string) =>
    api
      .post<Blob>(
        `/api/v1/admin/tax-clients/${clientId}/pdf-templates/${docType}/preview`,
        html ? { html } : undefined,
        { responseType: 'blob' },
      )
      .then((r) => r.data),

  /** Get del default Taxor (read-only). */
  getDefault: (docType: DocType) =>
    api
      .get<PdfTemplate>(`/api/v1/admin/pdf-templates/defaults/${docType}`)
      .then((r) => r.data),
}
